import { RaceTarget, PlannedWeek, PlannedSession, SportType, TrainingPhase, CalendarEvent, PreferredDays, CommuteConfig, FixedSession, BlockedRange, SPORTS } from "../types/training";
import { pickIntervalSession, RaceDistance, Specificity, BankSession } from "./workoutBank";
import { pickStrengthRoutine, buildGymDescription, strengthFocusLabel } from "./strengthRoutines";

// ── Seasonal sport substitution ───────────────────────────────────────────────

// NordicSki becomes RollerSki when there's no snow. The snow window wraps the
// year-end: snowStart..snowEnd inclusive (e.g. 11..4 = Nov–Apr). Defaults match
// a typical Nordic winter; configurable per athlete's region.
function isSnowMonth(month: number, snowStart = 11, snowEnd = 4): boolean {
  return snowStart <= snowEnd
    ? month >= snowStart && month <= snowEnd
    : month >= snowStart || month <= snowEnd;
}
function seasonalSport(sport: SportType, dateStr: string, snowStart = 11, snowEnd = 4): SportType {
  if (sport !== "NordicSki") return sport;
  const month = parseInt(dateStr.slice(5, 7), 10);
  return isSnowMonth(month, snowStart, snowEnd) ? "NordicSki" : "RollerSki";
}

// ── ISO week helpers ──────────────────────────────────────────────────────────

function isoWeekKey(date: Date): string {
  const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((utc.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${utc.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

function weekLabel(key: string): string {
  const m = key.match(/W(\d+)$/);
  return m ? `V${parseInt(m[1])}` : key;
}

function mondayOfISOWeek(weekKey: string): Date {
  const [yearStr, wStr] = weekKey.split("-W");
  const year = parseInt(yearStr);
  const week = parseInt(wStr);
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dow = (jan4.getUTCDay() + 6) % 7;
  const monday1 = new Date(jan4.getTime() - dow * 86400000);
  return new Date(monday1.getTime() + (week - 1) * 7 * 86400000);
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 86400000);
}

function toDateStr(date: Date): string {
  return date.toISOString().slice(0, 10);
}

// ── Analysis helpers ──────────────────────────────────────────────────────────

function parseDurationFromText(text: string): number {
  const t = text.toLowerCase();
  const h = t.match(/(\d+(?:[.,]\d+)?)\s*h(?:ours?)?\b/);
  if (h) {
    const rest = t.slice(t.indexOf(h[0]) + h[0].length);
    const m2 = rest.match(/^\s*(\d+)\s*m(?:in)?\b/);
    return parseFloat(h[1].replace(",", ".")) + (m2 ? parseInt(m2[1]) / 60 : 0);
  }
  const m = t.match(/(\d+)\s*m(?:in)?\b/);
  if (m) return parseInt(m[1]) / 60;
  return 0;
}

// ── Structured workout parser ─────────────────────────────────────────────────
// Parses intervals.icu-style structured descriptions into ordered segments,
// expanding "Nx" repeat blocks. Handles both our generated units (min/sek) and
// intervals.icu's native units (m/s/h). A BLANK line ends a repeat block (this
// is how warm-up/cool-down stay at ×1 even when they carry a leading "-").
//   -10m Z2 HR
//   10x
//   - 45s Z4 HR
//   - 90s Z1 HR
//   -10m Z2 HR
export interface WorkoutSegment { min: number; zone: number; }
const ZONE_IF: Record<number, number> = { 1: 0.55, 2: 0.68, 3: 0.78, 4: 0.87, 5: 0.97 };

export function parseWorkoutSegments(text: string): WorkoutSegment[] {
  const segs: WorkoutSegment[] = [];
  let mult = 1;
  let pending: WorkoutSegment[] = []; // sub-steps of the current repeat block

  // Repeat the collected block ×mult, INTERLEAVED (Z5,Z2,Z5,Z2,… not Z5×n,Z2×n).
  const flush = () => {
    for (let i = 0; i < mult; i++) pending.forEach(s => segs.push({ ...s }));
    pending = []; mult = 1;
  };

  for (const rawLine of text.split(/\n/)) {
    const line = rawLine.trim();
    if (line === "") { flush(); continue; }                  // blank ends a block

    const mx = line.match(/^(\d+)\s*[x×]$/i);
    if (mx) { flush(); mult = parseInt(mx[1]); continue; }

    // value + unit; accept sek/min/h and intervals.icu's s/m. Ignore "km".
    const dur = line.match(/(\d+(?:[.,]\d+)?)\s*(sek|min|h|s|m)\b/i);
    if (!dur || /\bkm\b/i.test(line)) { if (!line.startsWith("-")) flush(); continue; }
    const val = parseFloat(dur[1].replace(",", "."));
    const u = dur[2].toLowerCase();
    const min = u === "h" ? val * 60 : (u === "s" || u === "sek") ? val / 60 : val;

    const zoneM = line.match(/z([1-5])/i);
    const zone = zoneM ? parseInt(zoneM[1]) : 2;

    if (line.startsWith("-")) pending.push({ min, zone });   // sub-step → block
    else { flush(); segs.push({ min, zone }); }              // top-level step ×1
  }
  flush();
  return segs;
}

// Append "Press lap" to the warm-up and to the recovery segments between
// intervals, so they end on the Garmin lap button instead of a fixed time.
// Only applied to true interval workouts (those with an "Nx" repeat block); the
// work reps and the cool-down keep their fixed durations.
export function withPressLap(desc: string): string {
  if (!/\n\s*\d+\s*[x×]\s*$/im.test(desc)) return desc; // no repeat block → leave as-is
  let firstDur = true;
  return desc.split("\n").map(line => {
    const t = line.trim();
    if (t === "" || /^\d+\s*[x×]$/i.test(t)) return line;
    if (!/\d+(?:[.,]\d+)?\s*(sek|min|h|s|m)\b/i.test(t) || /\bkm\b/i.test(t)) return line;
    if (/press lap/i.test(t)) return line;
    const zone = (t.match(/z([1-5])/i)?.[1] ?? "2");
    const isSub = t.startsWith("-");
    const isRecovery = parseInt(zone) <= 2;
    if (!isSub) {
      const warmup = firstDur && isRecovery;
      firstDur = false;
      return warmup ? `${line} Press lap` : line;       // warm-up only (not cool-down)
    }
    return isRecovery ? `${line} Press lap` : line;       // rest between reps
  }).join("\n");
}

// intervals.icu separates a workout's sections (warm-up / each repeat block /
// cool-down) by BLANK LINES. Without them it glues adjacent lines together
// (e.g. "10min Z2 HR Press lap 6x"). Insert a blank line before every "Nx"
// repeat block and before a steady step that follows interval reps.
export function spaceIcuSections(desc: string): string {
  const lines = desc.split("\n").map(l => l.trimEnd()).filter(l => l.trim() !== "");
  const out: string[] = [];
  for (const line of lines) {
    const t = line.trim();
    const prev = out.length ? out[out.length - 1].trim() : "";
    const isNx = /^\d+\s*[x×]$/i.test(t);
    const isSub = t.startsWith("-");
    const prevIsSub = prev.startsWith("-");
    if (prev !== "" && (isNx || (!isSub && prevIsSub))) out.push("");
    out.push(line);
  }
  return out.join("\n");
}

// ── intervals.icu workout text format ─────────────────────────────────────────
// Authoritative rules (intervals.icu workout builder syntax):
//   • Every STEP line must start with "- ".
//   • Durations use h / m / s only — NOT "min" / "sek". Combined form "1h30m".
//   • A repeat block is a standalone "Nx" line (no dash) followed by its "- "
//     sub-steps, with a blank line before and after the block.
//   • Targets may be HR zones ("Z2 HR"), % HR, watts, pace. "Press lap" is an
//     export cue that makes a step end on the lap button — kept verbatim.
// Internally the workout bank emits top-level steps WITHOUT a dash and uses the
// Swedish "min"/"sek"; toIcuWorkout() is the single place that converts an
// internal description into strict intervals.icu text. Run it LAST, after
// withPressLap() and spaceIcuSections().

// A line begins with a duration/distance token → it is a workout step. No \b
// after the unit so combined forms like "1h30m" are still recognised.
const ICU_STEP_START = /^\d+(?:[.,]\d+)?\s*(h|m|s|km|mi|min|sek|sec)/i;
const ICU_REPEAT = /^\d+\s*[x×]$/i;
const ICU_BAD_UNIT = /\d\s*(min|sek|sec)\b/i;
// A bare "Z2" is read by intervals.icu as a POWER zone (flat 0w graph). All our
// targets are heart-rate zones, so a zone must be written "Z2 HR". This matches
// a zone NOT already followed by an HR/%/bpm qualifier.
const ICU_BARE_ZONE = /\bZ([1-5])\b(?!\s*(?:HR|%|bpm))/i;

// Convert "min"→"m", "sek"/"sec"→"s", and collapse "1h 30m"→"1h30m".
function normalizeIcuUnits(line: string): string {
  return line
    .replace(/(\d+(?:[.,]\d+)?)\s*min\b/gi, "$1m")
    .replace(/(\d+(?:[.,]\d+)?)\s*se[kc]\b/gi, "$1s")
    .replace(/(\d+)\s*h\s+(\d+)\s*m\b/gi, "$1h$2m");
}

// Append "HR" to any bare zone so it targets heart rate, not power.
function normalizeIcuZones(line: string): string {
  return line.replace(/\bZ([1-5])\b(?!\s*(?:HR|%|bpm))/gi, "Z$1 HR");
}

/**
 * Normalise an internal workout description into strict intervals.icu text:
 * dash-prefix every step, convert units to h/m/s, keep "Nx" repeat markers and
 * blank lines untouched, and leave genuine prose (lines starting with a letter)
 * as a step cue/note. Idempotent — running it twice is a no-op.
 */
export function toIcuWorkout(desc: string): string {
  return desc.split("\n").map(raw => {
    const line = normalizeIcuZones(normalizeIcuUnits(raw.replace(/\s+$/, "")));
    const t = line.trim();
    if (t === "") return "";
    if (ICU_REPEAT.test(t)) return t;                         // repeat marker
    if (t.startsWith("-")) return `- ${t.replace(/^-+\s*/, "")}`; // tidy existing dash
    if (ICU_STEP_START.test(t)) return `- ${t}`;              // top-level step → add dash
    return t;                                                  // prose / note line
  }).join("\n");
}

/**
 * Validate a description against intervals.icu rules. Returns a list of human
 * readable problems ([] = valid). Used by the workout-format test so a malformed
 * workout can never silently reach intervals.icu again.
 */
export function validateIcuWorkout(desc: string): string[] {
  const errors: string[] = [];
  desc.split("\n").forEach((raw, i) => {
    const t = raw.trim();
    const ln = i + 1;
    if (t === "") return;
    if (ICU_BAD_UNIT.test(t)) errors.push(`Line ${ln}: invalid unit, use h/m/s: "${t}"`);
    if (ICU_BARE_ZONE.test(t)) errors.push(`Line ${ln}: zone target missing HR (use "Zn HR"): "${t}"`);
    if (ICU_REPEAT.test(t)) return;                           // valid repeat marker
    if (ICU_STEP_START.test(t)) {                             // looks like a step…
      errors.push(`Line ${ln}: step missing "- " prefix: "${t}"`);
      return;
    }
    if (t.startsWith("-")) {                                  // dashed step must have a duration
      const body = t.replace(/^-+\s*/, "");
      if (!/\d+(?:[.,]\d+)?\s*(h|m|s|km|mi)\b/i.test(body))
        errors.push(`Line ${ln}: step has no duration/distance: "${t}"`);
      return;
    }
    // else: prose/cue line starting with a letter — allowed
  });
  return errors;
}

export function parseStructuredWorkout(text: string): { tss: number; durationHours: number } | null {
  if (text.split(/\n/).filter(l => l.trim()).length < 2) return null;
  const segs = parseWorkoutSegments(text);
  if (segs.length === 0) return null;
  let totalMin = 0, totalTSS = 0;
  for (const s of segs) {
    const if_ = ZONE_IF[s.zone] ?? 0.68;
    totalMin += s.min;
    totalTSS += if_ * if_ * (s.min / 60) * 100;
  }
  if (totalMin <= 0) return null;
  return { tss: Math.round(totalTSS), durationHours: Math.round(totalMin * 4 / 60) / 4 };
}

// ── PMC simulation ────────────────────────────────────────────────────────────
// Advances CTL and ATL by one week assuming constant daily TSS = weeklyTSS / 7.
// Uses exact exponential smoothing (τ_CTL = 42d, τ_ATL = 7d).
function advanceWeek(ctl: number, atl: number, weeklyTSS: number): { ctl: number; atl: number } {
  const d = weeklyTSS / 7;
  return {
    ctl: ctl * Math.exp(-7 / 42) + d * (1 - Math.exp(-7 / 42)),
    atl: atl * Math.exp(-7 / 7)  + d * (1 - Math.exp(-7 / 7)),
  };
}

function estimateEventTSS(e: CalendarEvent): number {
  const raw = e as any;
  // Try structured parser on description first (most accurate for interval workouts)
  const desc = (raw.description ?? "") as string;
  if (desc.includes("\n")) {
    const parsed = parseStructuredWorkout(desc);
    if (parsed) return parsed.tss;
  }
  // Fall back to duration + flat IF estimate
  const hours = raw.duration && raw.duration > 0
    ? raw.duration / 3600
    : parseDurationFromText(`${e.name ?? ""} ${desc}`);
  if (hours <= 0) return 0;
  const text = `${e.name ?? ""} ${desc}`;
  const HARD_KW = /interval|intervall|threshold|tr[öo]skel|tempo|z[45]|sprint|fartlek|backe|hill/i;
  const EASY_KW = /pendling|commute|l[äa]tt|easy|lugn|vila|recovery/i;
  const isHard = HARD_KW.test(text) && !EASY_KW.test(text);
  const isLong = hours >= 1.4 && !isHard;
  const if_ = isHard ? 0.88 : isLong ? 0.78 : 0.68;
  return Math.round(if_ * if_ * hours * 100);
}

function classifyEvent(e: CalendarEvent): "long" | "hard" | "easy" | "skip" {
  const name = e.name ?? "";
  const LOW = /pendling|commute|gym|styrka|strength/i;
  if (LOW.test(name)) return "skip";
  const text = `${name} ${(e as any).description ?? ""}`;
  const HARD_KW = /interval|intervall|threshold|tr[öo]skel|tempo|z[45]|sprint|fartlek|backe|hill/i;
  const EASY_KW = /l[äa]tt|easy|lugn|vila|recovery/i;
  if (HARD_KW.test(text) && !EASY_KW.test(text)) return "hard";
  const raw = e as any;
  const hours = raw.duration && raw.duration > 0
    ? raw.duration / 3600
    : parseDurationFromText(text);
  if (hours >= 1.4) return "long";
  return "easy";
}

// ── Interval structure parser ─────────────────────────────────────────────────
// Parses rep×duration patterns from event names/descriptions.
// Handles: "5×4min", "5x4min", "8×30sek", "3×10min med 3min vila"

interface IntervalStructure {
  reps: number;
  workMin: number;
  restMin: number | null;
  totalWorkMin: number;
}

function parseIntervalStructure(text: string): IntervalStructure | null {
  const t = text.toLowerCase();
  const m = t.match(/(\d+)\s*[×x]\s*(\d+(?:[.,]\d+)?)\s*(sek|min)/);
  if (!m) return null;
  const reps = parseInt(m[1]);
  const val  = parseFloat(m[2].replace(",", "."));
  const workMin = m[3] === "sek" ? val / 60 : val;
  if (reps < 2 || workMin <= 0) return null;

  // Rest: "Xmin vila", "vila Xmin", "/Xmin", "med Xmin vila", "Xsek vila"
  const restM = t.match(/(?:vila|rest|paus|\/\s*)(\d+(?:[.,]\d+)?)\s*(sek|min)/);
  const restMin = restM
    ? (restM[2] === "sek" ? parseFloat(restM[1]) / 60 : parseFloat(restM[1].replace(",", ".")))
    : null;

  return { reps, workMin, restMin, totalWorkMin: reps * workMin };
}

// ── Race distance inference ───────────────────────────────────────────────────
// Used to adapt interval type, long run cap, and session descriptions.

// RaceDistance imported from workoutBank

export function inferRaceDistance(name: string, sport?: SportType): RaceDistance {
  const n = name.toLowerCase();
  const isSki = sport === "NordicSki" || sport === "RollerSki";

  if (isSki) {
    // XC ski races are long endurance events — map by km (or famous race names)
    // to the high-volume profiles, not the running 5k/10k buckets. Profiles
    // mainly drive VOLUME + long-session length; ski-specific intervals come
    // from the workout bank (selected by sport).
    if (/vasaloppet|marcialonga|birke(n|bein)|engadin|dolomiten|nordenski|finlandia|könig|tartu|jizer|ski\s*classics|ski\s*marathon|skimaraton/.test(n)) return "ultra";
    const km = n.match(/(\d{1,3})\s*km\b/);
    if (km) {
      const k = parseInt(km[1]);
      if (k >= 70) return "ultra";
      if (k >= 35) return "marathon";
      if (k >= 18) return "half";
      return "10k";
    }
    if (/öppet\s*spår|halvvasan|tjejvasan/.test(n)) return "marathon";
    return "marathon"; // default ski race = marathon-like endurance volume
  }

  if (/\b(ultra|100k|50k|50\s*mi)\b/.test(n)) return "ultra";
  if (/\b(marathon|maraton|42|42\.2|26\.2)\b/.test(n) && !/half|halv|mini/.test(n)) return "marathon";
  if (/\b(half|halv|21|21\.1|13\.1)\b/.test(n)) return "half";
  if (/\b(10\s*k(m)?|10000)\b/.test(n)) return "10k";
  if (/\b([4-9]\s*k(m)?|5000)\b/.test(n)) return "5k";
  return "unknown";
}

// Rough finishing-time estimate from distance, used only to size the plan when
// the athlete gives km but no expected time. Trail is slower; ski covers km
// faster than running. These are citizen-pace ballparks, not race predictions.
export function expectedHoursFromKm(km: number, sport?: SportType, trail?: boolean): number {
  const kmh = sport === "Ride" ? 28
    : (sport === "NordicSki" || sport === "RollerSki") ? 13
    : trail ? 8 : 10.5; // run (road)
  return km / kmh;
}

// Map a continuous distance/duration onto the discrete profile band that drives
// interval structure + descriptions. The long-run cap is scaled separately and
// continuously (see resolveLongCap), so two races in the same band but different
// length still get differently-sized long runs.
export function kmToDistanceBand(km: number): RaceDistance {
  if (km <= 7) return "5k";
  if (km <= 15) return "10k";
  if (km <= 25) return "half";
  if (km <= 45) return "marathon";
  return "ultra";
}

function durationToDistanceBand(h: number): RaceDistance {
  if (h <= 0.5) return "5k";
  if (h <= 0.9) return "10k";
  if (h <= 1.8) return "half";
  if (h <= 3.5) return "marathon";
  return "ultra";
}

interface IntervalBlock {
  workMin: number;
  restMin: number;
  workZone: number;
  restZone: number;
  maxReps: number; // cap from science (Seiler optimal window etc.)
  minReps: number;
}

interface DistanceProfile {
  maxLongHours: number;
  targetPeakHours: number; // ideal peak weekly volume for a solid prep at this distance
  intervalIF: number;
  intervalName: string;
  intervalBlock: IntervalBlock;
  longDesc: (dur: string, sport: string) => string;
  easyDesc: (dur: string, sport: string) => string;
}

// Build a structured intervals.icu-format description from session duration.
// Format matches what intervals.icu parses as a structured workout:
//   10m Z2 HR
//   5x
//   - 4m Z5 HR
//   - 4m Z1 HR
//   10m Z1 HR
function buildStructuredIntervalDesc(
  profile: DistanceProfile,
  totalHours: number,
): string {
  const totalMin  = Math.round(totalHours * 60);
  const warmupMin = 10;
  const coolMin   = 10;
  const availMin  = Math.max(totalMin - warmupMin - coolMin, 0);
  const { workMin, restMin, workZone, restZone, maxReps, minReps } = profile.intervalBlock;
  const cycleMin  = workMin + restMin;
  const reps = Math.min(maxReps, Math.max(minReps, Math.floor(availMin / cycleMin)));
  const usedMin   = reps * cycleMin;
  const actualCool = Math.max(coolMin, totalMin - warmupMin - usedMin);

  const fmt = (m: number) => Number.isInteger(m) ? `${m}m` : `${Math.round(m)}m`;
  return [
    `${fmt(warmupMin)} Z2 HR`,
    `${reps}x`,
    `- ${fmt(workMin)} Z${workZone} HR`,
    `- ${fmt(restMin)} Z${restZone} HR`,
    `${fmt(actualCool)} Z1 HR`,
  ].join("\n");
}

const DISTANCE_PROFILES: Record<RaceDistance, DistanceProfile> = {
  "5k": {
    maxLongHours: 1.25,
    targetPeakHours: 6,
    intervalIF: 0.97,
    intervalName: "VO2max",
    intervalBlock: { workMin: 4, restMin: 4, workZone: 5, restZone: 1, maxReps: 6, minReps: 3 },
    longDesc: (d, s) => `${d} Z2 långpass. Aerob bas för 5K-specifik uthållighet. ${s}`,
    easyDesc: (d, s) => `${d} lätt Z1 återhämtning. Håll pulsen låg. ${s}`,
  },
  "10k": {
    maxLongHours: 1.5,
    targetPeakHours: 7,
    intervalIF: 0.95,
    intervalName: "VO2max",
    intervalBlock: { workMin: 4, restMin: 3, workZone: 5, restZone: 1, maxReps: 6, minReps: 3 },
    longDesc: (d, s) => `${d} Z2 långpass. Bygger aerob bas för 10K. ${s}`,
    easyDesc: (d, s) => `${d} lätt Z1 återhämtning. Aktiv vila. ${s}`,
  },
  "half": {
    maxLongHours: 1.75,
    targetPeakHours: 8.5,
    intervalIF: 0.92,
    intervalName: "Tempo",
    intervalBlock: { workMin: 10, restMin: 3, workZone: 4, restZone: 2, maxReps: 4, minReps: 2 },
    longDesc: (d, s) => `${d} Z2 långpass, sista 20m i halvmaratonfart. Tävlingsspecifik uthållighet. ${s}`,
    easyDesc: (d, s) => `${d} lätt Z1 återhämtning. Lugnt och kontrollerat. ${s}`,
  },
  "marathon": {
    maxLongHours: 2.5,
    targetPeakHours: 11,
    intervalIF: 0.91,
    intervalName: "Maratontempo",
    intervalBlock: { workMin: 10, restMin: 3, workZone: 4, restZone: 2, maxReps: 4, minReps: 2 },
    longDesc: (d, s) => `${d} Z2 maratonlångpass, sista 30m i maratontempo. Den viktigaste sessionen för maratonförberedelse. ${s}`,
    easyDesc: (d, s) => `${d} lätt Z1 återhämtning. Viktigt att hålla sig lätt — volymen är hög. ${s}`,
  },
  "ultra": {
    maxLongHours: 3.5,
    targetPeakHours: 15,
    intervalIF: 0.88,
    intervalName: "Tempo",
    intervalBlock: { workMin: 20, restMin: 5, workZone: 3, restZone: 1, maxReps: 3, minReps: 2 },
    longDesc: (d, s) => `${d} Z2 ultralong — extremt lugnt, tidsbas framför allt. ${s}`,
    easyDesc: (d, s) => `${d} lätt Z1 återhämtning. ${s}`,
  },
  "unknown": {
    maxLongHours: 2.0,
    targetPeakHours: 8,
    intervalIF: 0.93,
    intervalName: "Interval",
    intervalBlock: { workMin: 4, restMin: 4, workZone: 4, restZone: 1, maxReps: 6, minReps: 3 },
    longDesc: (d, s) => `${d} Z2 aerobiskt långpass. Bygger aerob bas. ${s}`,
    easyDesc: (d, s) => `${d} lätt Z1 återhämtning. ${s}`,
  },
};

// ── Session templates ─────────────────────────────────────────────────────────

interface SessionTemplate {
  name: string;
  tssShare: number;
  candidateDays: number[]; // try in order; skip occupied days
  ifValue: number;
  canShareDay?: boolean;        // a second (double) session — may stack on a day that already has one
  forceSpecificity?: Specificity; // override phase specificity for bank selection (e.g. force threshold)
  seedOffset?: number;          // vary bank rotation so stacked sessions differ
}

// ── Polarization formula ──────────────────────────────────────────────────────
// The ~80/20 easy/hard split comes from Seiler's intensity-distribution work,
// much of it sampled from elite XC skiers — the canonical polarized population:
//   Seiler & Kjerland (2006), Quantifying training intensity distribution in
//     elite endurance athletes (Scand J Med Sci Sports).
//   Seiler & Tønnessen (2009), Intervals, thresholds, and long slow distance.
//   Seiler (2010), What is best practice for training intensity/duration
//     distribution? (IJSPP).
// Less trained athletes need even less intensity.
// TSB (form) adjusts the ratio: very negative TSB → pull back intensity.

export function calcIntensityRatio(ctl: number, tsb: number | null): number {
  let base: number;
  if (ctl < 30)      base = 0.05;
  else if (ctl < 50) base = 0.10;
  else if (ctl < 70) base = 0.15;
  else               base = 0.20;

  if (tsb !== null) {
    if (tsb < -25)      base *= 0.40;
    else if (tsb < -15) base *= 0.65;
    else if (tsb < -5)  base *= 0.85;
    else if (tsb > 10)  base = Math.min(base * 1.15, 0.22);
  }

  return Math.min(Math.max(base, 0.03), 0.22);
}

// Tuned IFs for the main endurance sports; every other sport defaults by whether
// it's endurance (so the expanded sport list works without hand-tuning each one).
type SportIF = { long: number; interval: number; easy: number };
const CUSTOM_IFS: Partial<Record<SportType, SportIF>> = {
  Run:        { long: 0.81, interval: 0.93, easy: 0.68 },
  TrailRun:   { long: 0.80, interval: 0.92, easy: 0.68 },
  Ride:       { long: 0.71, interval: 0.87, easy: 0.65 },
  MountainBike:{ long: 0.74, interval: 0.88, easy: 0.66 },
  GravelRide: { long: 0.72, interval: 0.87, easy: 0.65 },
  Swim:       { long: 0.80, interval: 0.90, easy: 0.70 },
  NordicSki:  { long: 0.80, interval: 0.90, easy: 0.68 },
  RollerSki:  { long: 0.78, interval: 0.88, easy: 0.67 },
  Rowing:     { long: 0.78, interval: 0.90, easy: 0.68 },
};
const ENDURANCE_DEFAULT_IF: SportIF = { long: 0.78, interval: 0.88, easy: 0.68 };
const NON_ENDURANCE_IF: SportIF = { long: 0.70, interval: 0.85, easy: 0.65 };
const SPORT_IFS = Object.fromEntries(
  SPORTS.map(s => [s.value, CUSTOM_IFS[s.value] ?? (s.category === "endurance" ? ENDURANCE_DEFAULT_IF : NON_ENDURANCE_IF)]),
) as Record<SportType, SportIF>;

const DEFAULT_DAYS: PreferredDays = { long: [5], hard: [1], easy: [0, 3], gym: [] };

// Preferred days are an ordered availability pool.
// The planner picks the first N days based on how many sessions are needed.
function buildTemplates(
  sport: SportType,
  intensityRatio: number,
  distProfile: DistanceProfile,
  days: PreferredDays = DEFAULT_DAYS,
  ctl = 50,
  maxHard?: number,
  allowDoubles = false,
  doubleThreshold = false,
): SessionTemplate[] {
  const ifs = SPORT_IFS[sport] ?? SPORT_IFS["Run"];
  const aerobic = 1 - intensityRatio;
  const templates: SessionTemplate[] = [];
  // Controlled sub-threshold IF for the Norwegian double-threshold day — held
  // below race-pace VO2max work (Z4 ~lactate 2.5–4 mmol), the point being to
  // accumulate threshold VOLUME without the strain of all-out intervals.
  const CONTROLLED_THRESHOLD_IF = 0.88;

  const nLong = days.long.length > 0 ? 1 : 0;
  // Two quality sessions per week once there's a base to handle it (ctl ≥ 45)
  // and the athlete marked two hard days; base phase is capped to one via maxHard.
  let nHard = days.hard.length > 0 ? (ctl >= 45 && days.hard.length >= 2 ? 2 : 1) : 0;
  if (maxHard != null) nHard = Math.min(nHard, maxHard);
  // Double-threshold: one quality DAY carrying two controlled threshold
  // sessions (AM/PM), rather than spreading quality over more days.
  if (doubleThreshold) nHard = 1;
  // Use every easy day the athlete marked as available. Total volume is governed
  // by the weekly hour ramp (which is CTL-aware), so more easy slots simply let
  // the plan reach the target volume in a polarized way rather than inflating it.
  const nEasy = days.easy.length;
  // Double days: a second easy session on each easy day. These share the SAME
  // total easy share (split over more slots), so long/hard proportions are
  // unchanged — the extra slots just give the volume somewhere to land when a
  // single session per day can't absorb the weekly target.
  const nDouble = allowDoubles ? days.easy.length : 0;
  const nEasyTotal = nEasy + nDouble;

  const longShare = nLong > 0 ? aerobic * 0.55 : 0;
  const hardShare = nHard > 0 ? intensityRatio / nHard : 0;
  const easyShare = nEasyTotal > 0 ? (aerobic * 0.45) / nEasyTotal : 0;

  // Use distance-profile IF for intervals
  const intervalIF = distProfile.intervalIF;

  // All preferred days for each type are kept as candidates — the placer tries
  // them in order and picks the first available slot. This way preferred days
  // are a ranked preference pool, not a rigid fixed schedule.
  for (let i = 0; i < nLong; i++)
    templates.push({ name: "Long Z2",                  tssShare: longShare, candidateDays: days.long, ifValue: ifs.long });

  if (doubleThreshold) {
    // Two controlled threshold sessions on the same hard day (Norwegian model).
    templates.push({ name: distProfile.intervalName, tssShare: hardShare, candidateDays: days.hard, ifValue: CONTROLLED_THRESHOLD_IF, forceSpecificity: "threshold" });
    templates.push({ name: distProfile.intervalName, tssShare: hardShare, candidateDays: days.hard, ifValue: CONTROLLED_THRESHOLD_IF, forceSpecificity: "threshold", canShareDay: true, seedOffset: 3 });
  } else {
    for (let i = 0; i < nHard; i++)
      templates.push({ name: distProfile.intervalName, tssShare: hardShare, candidateDays: days.hard, ifValue: intervalIF });
  }

  for (let i = 0; i < nEasy; i++)
    templates.push({ name: "Easy",                     tssShare: easyShare, candidateDays: days.easy, ifValue: ifs.easy });
  for (let i = 0; i < nDouble; i++)
    templates.push({ name: "Easy",                     tssShare: easyShare, candidateDays: days.easy, ifValue: ifs.easy, canShareDay: true });

  return templates;
}

// Per-session duration cap (hours). Long uses the distance-specific max, or a
// caller-supplied progressive cap (so the long run grows from recent history).
function sessionCap(name: string, distProfile: DistanceProfile, longCap?: number): number {
  if (name === "Long Z2") return longCap ?? distProfile.maxLongHours;
  if (name === "Easy")    return 1.5;
  return 4.0; // hard / interval
}

export interface WeekAllocation {
  perTemplate: { tmpl: SessionTemplate; hours: number; tss: number }[];
  totalHours: number;
  totalTSS: number;
}

// Distribute a weekly HOUR budget across the week's sessions, respecting
// per-session caps. Long and hard get their share first (capped); ALL remaining
// volume goes to easy running (this is what makes the week polarized). Easy
// volume fills PRIMARY easy days toward their cap first, and only spills into
// double (canShareDay) slots when single sessions can't absorb it — so doubles
// appear only when the week genuinely needs them, not in low-volume weeks.
// Anything still unschedulable is dropped; totalHours reflects the achievable
// volume honestly, and TSS is derived from each session's real IF + duration.
function allocateWeek(weekHours: number, templates: SessionTemplate[], distProfile: DistanceProfile, longCap?: number, preUsedEasy = 0): WeekAllocation {
  const totalShare = templates.reduce((s, t) => s + t.tssShare, 0) || 1;
  const items = templates.map(t => ({
    tmpl: t,
    cap: sessionCap(t.name, distProfile, longCap),
    hours: 0,
  }));

  // Long + hard: take their share of the budget, capped.
  let usedNonEasy = 0;
  for (const it of items) {
    if (it.tmpl.name === "Easy") continue;
    it.hours = Math.min(weekHours * (it.tmpl.tssShare / totalShare), it.cap);
    usedNonEasy += it.hours;
  }

  // Easy soaks up everything that's left (incl. long-run overflow), filling
  // primary easy days first, then doubles. Greedy even fill, cap-aware.
  const fill = (slots: typeof items, target: number): number => {
    let remaining = target;
    let active = slots.filter(s => s.hours < s.cap - 1e-9);
    while (remaining > 0.01 && active.length > 0) {
      const per = remaining / active.length;
      let cappedAny = false;
      for (const s of active) {
        const add = Math.min(per, s.cap - s.hours);
        s.hours += add; remaining -= add;
        if (s.hours >= s.cap - 1e-9) cappedAny = true;
      }
      if (!cappedAny) break;
      active = active.filter(s => s.hours < s.cap - 1e-9);
    }
    return remaining;
  };

  // No easy session under 45 min: cap how many easy slots we actually use so each
  // clears the floor. Rather than spreading low volume into several 15-min runs,
  // group it into fewer ≥45-min sessions (extra slots simply stay empty).
  const MIN_EASY = 0.75;
  // Long + hard take their share of the FULL weekly volume; commute/fixed volume
  // (preUsedEasy) only displaces EASY filler — it must never shrink the long run.
  const easyTarget = Math.max(weekHours - usedNonEasy - preUsedEasy, 0);
  const primary = items.filter(s => s.tmpl.name === "Easy" && !s.tmpl.canShareDay);
  const doubles = items.filter(s => s.tmpl.name === "Easy" && s.tmpl.canShareDay);
  // When doubles are enabled, PREFER two-a-days: spread easy volume across the
  // primary AND second (AM/PM) slots (primaries first, so each day gets a single
  // before a second), each session ≥ MIN_EASY. More frequency, same total volume.
  // Without doubles, fill one-per-day only (fewer, longer sessions).
  const pool = doubles.length > 0 ? [...primary, ...doubles] : primary;
  const nUse = Math.min(pool.length, Math.floor(easyTarget / MIN_EASY));
  fill(pool.slice(0, nUse), easyTarget);

  const perTemplate = items.map(a => ({
    tmpl: a.tmpl,
    hours: Math.round(a.hours * 4) / 4,
    tss: Math.round(a.tmpl.ifValue * a.tmpl.ifValue * 100 * a.hours),
  }));

  return {
    perTemplate,
    totalHours: Math.round(perTemplate.reduce((s, a) => s + a.hours, 0) * 4) / 4,
    totalTSS: perTemplate.reduce((s, a) => s + a.tss, 0),
  };
}

// Lowercased sport labels for inline use in session descriptions (derived).
const SPORT_LABELS = Object.fromEntries(
  SPORTS.map(s => [s.value, s.label.toLowerCase()]),
) as Record<SportType, string>;

function formatDuration(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h > 0 && m > 0) return `${h}h ${m}min`;
  if (h > 0) return `${h}h`;
  return `${m}min`;
}

function makeSession(
  tmpl: SessionTemplate,
  sessionHours: number,
  weekKey: string,
  sport: SportType,
  distProfile: DistanceProfile,
  todayStr: string,
  occupiedDays: Set<string>,
  qualityDayBuf: Set<string> = new Set(),
  specificity: Specificity = "threshold",
  dist: RaceDistance = "unknown",
  ctl: number = 50,
  dayCount?: Map<string, number>,
  preferHills = false,
  extraSessions: BankSession[] = [],     // user-added custom interval sessions
  favoriteIds: Set<string> = new Set(),  // starred sessions, preferred by the picker
): PlannedSession | null {
  // sessionHours is the pre-allocated, cap-aware duration for this session.
  const rawDuration = sport === "WeightTraining" ? 1.0 : sessionHours;
  if (rawDuration < 0.083) return null; // < 5 min
  // No easy session under 45 min — its volume is consolidated elsewhere (allocateWeek).
  if (tmpl.name === "Easy" && rawDuration < 0.75) return null;

  let tss = Math.round(tmpl.ifValue * tmpl.ifValue * rawDuration * 100);

  // Safety cap (allocation already enforces these, but keep makeSession honest).
  const maxHours = tmpl.name === "Long Z2"
    ? distProfile.maxLongHours
    : tmpl.name === "Easy" ? 1.5 : 4.0;
  let durationHours = Math.min(rawDuration, maxHours);

  // Try each candidate day in preference order; skip blocked or adjacent-quality
  // days. Two passes: first AVOID the day-after-a-quality/long buffer (so we
  // don't stack two big days back-to-back — a coach spreads them out), then
  // fall back to allowing it only if the athlete's available days leave no
  // choice. Long runs never go adjacent to quality (hard rule, both passes).
  const monday = mondayOfISOWeek(weekKey);
  const isLongRun = tmpl.name === "Long Z2";
  const countOn = (d: string) => dayCount?.get(d) ?? (occupiedDays.has(d) ? 1 : 0);
  let date: string | null = null;
  for (const avoidBuffer of [true, false]) {
    for (const dayOffset of tmpl.candidateDays) {
      const d = toDateStr(addDays(monday, dayOffset));
      if (d < todayStr) continue;
      const cnt = countOn(d);
      if (tmpl.canShareDay) {
        if (cnt !== 1) continue; // double: stack where exactly one session sits
      } else {
        if (cnt >= 1) continue;
        if (isLongRun && qualityDayBuf.has(d)) continue;          // long: never adjacent
        if (avoidBuffer && qualityDayBuf.has(d)) continue;        // others: prefer not adjacent
      }
      date = d;
      break;
    }
    if (date || tmpl.canShareDay) break; // doubles don't need the fallback pass
  }
  if (!date) return null;

  // If an easy session had to land right after a long/quality day (no other day
  // available), make it a short recovery run rather than a second big session.
  if (tmpl.name === "Easy" && qualityDayBuf.has(date)) {
    durationHours = Math.min(durationHours, 0.75);
  }

  const dur = formatDuration(Math.round(durationHours * 4) / 4);
  const sportLabel = SPORT_LABELS[sport] ?? sport;

  let sessionName = tmpl.name;
  let description: string;
  if (tmpl.name === "Long Z2") {
    description = toIcuWorkout(distProfile.longDesc(dur, sportLabel));
  } else if (tmpl.name === "Easy") {
    description = toIcuWorkout(distProfile.easyDesc(dur, sportLabel));
  } else {
    const picked = pickIntervalSession(sport, tmpl.forceSpecificity ?? specificity, dist, ctl, Math.round(durationHours * 4) / 4, date, tmpl.seedOffset ?? 0, preferHills, extraSessions, favoriteIds);
    sessionName = picked.name;
    // Normalise to strict intervals.icu format (dash steps, h/m/s units).
    description = toIcuWorkout(spaceIcuSections(withPressLap(picked.description)));
    // Parse the description to get the actual duration and TSS it encodes.
    // The bank's generate() may produce a session longer than durationHours
    // (e.g. fixed-rep neuromuscular sessions have a minimum sensible length).
    const parsed = parseStructuredWorkout(description);
    if (parsed && parsed.durationHours > 0) {
      durationHours = parsed.durationHours;
      tss = parsed.tss;
    }
  }

  return {
    name: sessionName,
    date,
    weekKey,
    sport,
    durationHours: Math.round(durationHours * 4) / 4,
    targetIF: tmpl.ifValue,
    estimatedTSS: tss,
    description,
  };
}

// ── Periodization ─────────────────────────────────────────────────────────────

/**
 * Clamp where a race's plan starts when races are chained. The default recovery
 * (≈2 weeks after the previous race) can overshoot a closely-following race and
 * leave it with no runway ("Loppet är för nära…"). Guarantee at least ~1 week
 * before the race, never before the previous race, and never in the past.
 */
export function clampPlanStart(desired: Date, raceDate: Date, prevRaceDate: Date | null, today: Date): Date {
  let s = desired.getTime();
  const latest = raceDate.getTime() - 7 * 86400000; // ≥ ~1 week of runway
  if (s > latest) s = latest;
  if (prevRaceDate && s < prevRaceDate.getTime()) s = prevRaceDate.getTime();
  if (s < today.getTime()) s = today.getTime();
  return new Date(s);
}

export function generateTrainingPlan(opts: {
  race: RaceTarget;
  currentCTL: number;
  currentTSB?: number | null;
  today: Date;
  startFrom?: Date;
  existingEvents?: CalendarEvent[];
  preferredDays?: PreferredDays;
  maxWeeklyHours?: number;
  planningMode?: "tss" | "duration";
  sportsEnjoyed?: SportType[];
  commute?: CommuteConfig;
  recentWeeklyHours?: number;   // avg weekly hours over the last 6 weeks (current form)
  recentLongestHours?: number;  // longest single endurance session over the year
  peakAchievedHours?: number;   // demonstrated sustainable weekly volume over the year
  historicalHardFraction?: number; // athlete's actual Z4+ share of load over the year
  tuneUpRaces?: { date: string; name: string }[]; // B/C races within this plan
  allowDoubles?: boolean;       // permit a second easy session on a day
  doubleThreshold?: boolean;    // Norwegian double-threshold day (advanced)
  primarySport?: SportType;     // the athlete's main sport — kept in view via easy cross-training
  snowStartMonth?: number;      // month snow arrives (1-12), default 11
  snowEndMonth?: number;        // month snow leaves (1-12), default 4
  fixedSessions?: FixedSession[]; // non-negotiable recurring commitments
  blockedRanges?: BlockedRange[]; // vacation / unavailable date ranges
  sickSince?: string | null;      // currently sick since this date (rest until recovery)
  ownEventIds?: number[];         // intervals.icu event IDs the planner itself published
  customSessions?: BankSession[];   // user-added interval sessions, merged into the bank
  favoriteSessionIds?: string[];    // starred sessions the picker prefers within their tier
}): { weeks: PlannedWeek[]; sessions: PlannedSession[]; flaggedDates: Map<string, string>; expectedRaceTSB: number; volumeNote?: string; plannedHardPct: number; historicalHardPct?: number } {
  const { race, currentTSB = null } = opts;
  // Guard against a NaN/invalid CTL (e.g. missing wellness) poisoning the ramp.
  const currentCTL = Number.isFinite(opts.currentCTL) && opts.currentCTL > 0 ? opts.currentCTL : 50;
  // The PLAN is the source of truth: only races are treated as fixed on the
  // calendar. All other existing events are ignored when planning (and wiped on
  // publish), so the plan is complete and publishing makes intervals.icu match
  // it exactly — except races, which are never touched.
  const existingEvents = (opts.existingEvents ?? []).filter(e => /^RACE/i.test((e as any).category ?? ""));
  const today = opts.startFrom ?? opts.today;
  const raceDate = new Date(race.date + "T12:00:00");
  const todayStr = toDateStr(opts.today);

  // Gym and commute are low-intensity — don't block endurance placement.
  const LOW_INTENSITY_EVENT = /pendling|commute|gym|styrka|strength/i;
  // Long/hard existing sessions need a recovery buffer — don't place a new
  // long run on the day before or after an existing quality session.
  const LONG_OR_HARD = /long|l[åa]ng|intervall?|interval|threshold|tr[öo]skel|tempo|z[45]/i;

  const occupiedDays   = new Set<string>(); // blocks exact-day placement
  const qualityDayBuf  = new Set<string>(); // blocks long-run placement on adjacent days
  const dayCount       = new Map<string, number>(); // sessions already placed per day (for doubles)
  const bump = (d: string) => dayCount.set(d, (dayCount.get(d) ?? 0) + 1);

  function adjacentDates(dateStr: string): string[] {
    const d = new Date(dateStr + "T12:00:00").getTime();
    return [
      new Date(d - 86400000).toISOString().slice(0, 10),
      new Date(d + 86400000).toISOString().slice(0, 10),
    ];
  }

  for (const e of existingEvents) {
    const name = e.name ?? "";
    if (LOW_INTENSITY_EVENT.test(name)) continue;
    const date = e.start_date_local.slice(0, 10);
    occupiedDays.add(date);
    bump(date);
    if (LONG_OR_HARD.test(name)) {
      qualityDayBuf.add(date);
      adjacentDates(date).forEach(d => qualityDayBuf.add(d));
    }
  }

  const flaggedDates = new Map<string, string>(); // date → Swedish reason string

  // Tune-up (B/C) races within this plan: treat as a hard race effort with a
  // short mini-taper. Keep the race day + the day before clear, block quality
  // on adjacent days, and flag it. The full periodization is NOT restarted.
  const dayBefore = (d: string) => new Date(new Date(d + "T12:00:00").getTime() - 86400000).toISOString().slice(0, 10);
  for (const t of opts.tuneUpRaces ?? []) {
    occupiedDays.add(t.date);
    occupiedDays.add(dayBefore(t.date));      // rest/shakeout day before
    bump(t.date); bump(dayBefore(t.date));
    qualityDayBuf.add(t.date);
    adjacentDates(t.date).forEach(d => qualityDayBuf.add(d));
    flaggedDates.set(t.date, `${t.name} (B-lopp) — kör som hård insats. Kort nedtrappning 1–2 dagar innan, ingen full vila. Återuppta planen direkt efteråt.`);
  }

  const msPerWeek = 7 * 86400000;
  // Anchor to whole weeks so the FINAL week contains the race date. Counting
  // ceil(days/7) from an arbitrary weekday left the race week misaligned (the
  // plan ended up to ~10 days before the race). We also skip the current week
  // when none of its training days are still ahead of us, so the plan doesn't
  // open with a dead, empty week.
  const todayOffset = (today.getDay() + 6) % 7; // Mon=0
  const pd = opts.preferredDays ?? DEFAULT_DAYS;
  const usableThisWeek = [...pd.long, ...pd.hard, ...pd.easy].some(o => o >= todayOffset);
  let startMonday = mondayOfISOWeek(isoWeekKey(today));
  if (!usableThisWeek) startMonday = addDays(startMonday, 7);
  const raceMonday = mondayOfISOWeek(isoWeekKey(raceDate));
  const weeksToRace = Math.round((raceMonday.getTime() - startMonday.getTime()) / msPerWeek) + 1;
  if (weeksToRace <= 0) return { weeks: [], sessions: [], flaggedDates, expectedRaceTSB: 0, plannedHardPct: 0 };

  // Blocked dates (vacation etc.): no sessions are placed on them, and a week's
  // volume is scaled down by the fraction of its training days that are blocked.
  const blockedDates = new Set<string>();
  // Recorded ranges (vacation + recovered illness) — these get a return ramp.
  const blockEnds: { end: string; days: number }[] = [];
  for (const r of opts.blockedRanges ?? []) {
    let d = new Date(r.start + "T12:00:00");
    const end = new Date(r.end + "T12:00:00");
    let n = 0;
    while (d <= end) { blockedDates.add(toDateStr(d)); d = new Date(d.getTime() + 86400000); n++; }
    blockEnds.push({ end: r.end, days: n });
  }
  // Currently sick: block from the onset through today (rolling, no end yet).
  if (opts.sickSince) {
    let d = new Date(opts.sickSince + "T12:00:00");
    const end = opts.today;
    while (d <= end) { blockedDates.add(toDateStr(d)); d = new Date(d.getTime() + 86400000); }
  }
  blockedDates.forEach(d => occupiedDays.add(d)); // block placement on these days

  // Return-to-training ramp: the first weeks AFTER a block of ≥3 days come back
  // gradually (≈55% → 78% → full) rather than jumping to the scheduled volume —
  // important after illness, useful after a holiday.
  function returnScaleForWeek(i: number): number {
    if (blockEnds.length === 0) return 1;
    const monday = addDays(startMonday, i * 7).getTime();
    let scale = 1;
    for (const b of blockEnds) {
      if (b.days < 3) continue;
      const endMs = new Date(b.end + "T12:00:00").getTime();
      if (monday <= endMs) continue;
      const weeksAfter = Math.floor((monday - endMs) / (7 * 86400000));
      if (weeksAfter === 0) scale = Math.min(scale, 0.55);
      else if (weeksAfter === 1) scale = Math.min(scale, 0.78);
    }
    return scale;
  }
  // Flag the athlete's already-planned sessions that fall in a blocked range —
  // they should be removed or moved since they're away.
  for (const e of existingEvents) {
    const d = e.start_date_local.slice(0, 10);
    if (blockedDates.has(d) && !/^RACE/i.test(e.category ?? "")) {
      flaggedDates.set(d, "Du är ledig denna period — ta bort eller flytta detta pass.");
    }
  }
  const trainingOffsets = Array.from(new Set([...pd.long, ...pd.hard, ...pd.easy]));
  function blockScaleForWeek(i: number): number {
    if (blockedDates.size === 0 || trainingOffsets.length === 0) return 1;
    const monday = addDays(startMonday, i * 7);
    const blocked = trainingOffsets.filter(o => blockedDates.has(toDateStr(addDays(monday, o)))).length;
    return 1 - blocked / trainingOffsets.length;
  }

  // Resolve how demanding the race is from whatever the athlete gave us.
  // Expected finishing time (explicit, else estimated from km) sizes the long
  // run continuously; the band only picks the interval structure/descriptions.
  const trail = !!race.trail;
  const expHours = race.durationHours
    ?? (race.distanceKm != null ? expectedHoursFromKm(race.distanceKm, race.sport, trail) : undefined);
  const dist: RaceDistance =
    race.distance
    ?? (race.distanceKm != null ? kmToDistanceBand(race.distanceKm) : undefined)
    ?? (expHours != null ? durationToDistanceBand(expHours) : undefined)
    ?? inferRaceDistance(race.name, race.sport);
  let distProfile = DISTANCE_PROFILES[dist];
  // Continuous long-run cap from the expected race time (time-on-feet ceiling 4h).
  if (expHours != null) {
    distProfile = { ...distProfile, maxLongHours: Math.max(1.0, Math.min(4.0, expHours)) };
  }
  // Trail/terrain: long runs become hilly time-on-feet (hill intervals are
  // preferred separately, see preferHills below).
  if (trail) {
    const baseLongDesc = distProfile.longDesc;
    distProfile = { ...distProfile, longDesc: (d, s) => `${baseLongDesc(d, s)} Kuperad terräng — sök backar och träna klättring.` };
  }

  // XC-ski-specific volume. Ski races map onto the running buckets for session
  // selection, but ski training carries MORE aerobic volume and longer LSD
  // sessions than the equivalent running event. Numbers below are for committed
  // amateurs (the app's audience): elite XC skiers train ~750–950 h/yr ≈ 15–20
  // h/wk (Sandbakk & Holmberg 2017), so amateurs sit below that; Vasaloppet prep
  // guidance puts long LSD sessions at 90 min–3 h. Capped by the athlete's own
  // demonstrated capacity and available time regardless.
  if (race.sport === "NordicSki" || race.sport === "RollerSki") {
    const SKI_VOL: Record<string, { peak: number; long: number }> = {
      ultra:    { peak: 14, long: 3.0 },  // Vasaloppet / ≥70 km marathon
      marathon: { peak: 12, long: 2.75 }, // 35–70 km
      half:     { peak: 11, long: 2.5 },  // 18–35 km
      "10k":    { peak: 9,  long: 2.0 },  // < 18 km / distance race
      "5k":     { peak: 8,  long: 1.75 },
      unknown:  { peak: 11, long: 2.5 },
    };
    const v = SKI_VOL[dist] ?? SKI_VOL.unknown;
    distProfile = { ...distProfile, targetPeakHours: v.peak, maxLongHours: v.long };
  }

  // ── Phase assignment ────────────────────────────────────────────────────────
  // base (aerobic, lowest intensity) → build (rising intensity) → peak
  // (race-specific) → taper → race. Base is the longest block and intensity is
  // introduced progressively (Lydiard / Daniels / Bompa).
  let baseWeeks: number, buildWeeks: number, peakWeeks: number, taperWeeks: number;
  if (weeksToRace <= 2) {
    baseWeeks = 0; buildWeeks = 0; peakWeeks = 0; taperWeeks = Math.max(weeksToRace - 1, 0);
  } else if (weeksToRace <= 5) {
    taperWeeks = 2; peakWeeks = 1;
    const load = weeksToRace - taperWeeks - peakWeeks - 1;
    baseWeeks = Math.round(load * 0.5); buildWeeks = load - baseWeeks;
  } else {
    taperWeeks = 2; peakWeeks = 2;
    const load = weeksToRace - taperWeeks - peakWeeks - 1;
    baseWeeks = Math.round(load * 0.55); buildWeeks = load - baseWeeks;
  }

  const mode = opts.planningMode ?? "tss";
  const loadWeeks = baseWeeks + buildWeeks;

  // Phase sequence — cutback every 4th week within the load block (Issurin 2010).
  const phases: TrainingPhase[] = [];
  for (let i = 0; i < loadWeeks; i++) {
    if (i % 4 === 3) phases.push("cutback");
    else phases.push(i < baseWeeks ? "base" : "build");
  }
  for (let i = 0; i < peakWeeks; i++) phases.push("peak");
  for (let i = 0; i < taperWeeks; i++) phases.push("taper");
  phases.push("race");

  // ── Volume ramp (hours is the source of truth; TSS is derived) ───────────────
  const AVG_TSS_PER_HOUR = 60; // realistic for a polarized mix (Seiler)
  // maxWeeklyHours is a CEILING (time the athlete has), not a target.
  const availableHours = opts.maxWeeklyHours ?? Infinity;

  // Start volume reflects ACTUAL recent training when we have it, not just the
  // CTL estimate — so week 1 matches what the athlete has really been doing.
  const fin = (n: number | undefined, d: number) => (Number.isFinite(n) ? (n as number) : d);
  const ctlHours = Math.max((currentCTL * 7) / AVG_TSS_PER_HOUR, 1.5);
  const recentWeekly = fin(opts.recentWeeklyHours, 0);
  const currentHours = recentWeekly > 0 ? Math.max(recentWeekly, 1.5) : ctlHours;

  const loadingWeeks = Math.max(loadWeeks - Math.floor(loadWeeks / 4), 1);
  const rampFactor = Math.pow(1.08, loadingWeeks);

  // Peak volume = what the GOAL needs, but never more than the athlete can
  // safely build to over the available runway, nor more than their time allows.
  // Rebuilding to a volume you've HELD before (over the past year) is safe and
  // faster than building it net-new, so demonstrated capacity raises the
  // reachable ceiling — a recent quiet block no longer suppresses the plan.
  const neededHours = distProfile.targetPeakHours;            // ideal for this race
  const reachableHours = Math.max(
    currentHours * rampFactor,                                // safe 8%/week build from now
    fin(opts.peakAchievedHours, 0),                           // …or back to a held volume
  );
  const peakHours = Math.min(neededHours, reachableHours, availableHours);
  const startHours = Math.min(Math.max(peakHours / rampFactor, currentHours), peakHours);

  const weekHoursTarget: number[] = phases.map((phase, i) => {
    let h: number;
    if (phase === "base" || phase === "build") {
      const li = i - Math.floor(i / 4); // discount cutbacks from the ramp
      h = Math.min(startHours * Math.pow(1.08, li), peakHours);
    } else if (phase === "cutback") {
      const li = i - Math.floor(i / 4) - 1;
      h = Math.min(startHours * Math.pow(1.08, Math.max(li - 1, 0)), peakHours) * 0.65;
    } else if (phase === "peak") {
      h = peakHours;
    } else {
      return 0; // taper / race set in pass 2
    }
    return h * blockScaleForWeek(i) * returnScaleForWeek(i); // blocked days + return ramp
  });

  // ── Fixed (non-negotiable) sessions ───────────────────────────────────────────
  // Resolve which fixed sessions land in each plan week. Biweekly ones fire on
  // even week indices (this week, then every other). Endurance fixed sessions
  // (easy/quality) reduce that week's volume budget so the plan doesn't pile
  // training on top of them; gym is strength and counts only toward load (TSS).
  const FIXED_IF: Record<string, number> = { easy: 0.68, quality: 0.86, gym: 0.60 };
  interface FixedInstance { fs: FixedSession; dayOffset: number; hours: number; tss: number; endurance: boolean; }
  function fixedForWeek(i: number): FixedInstance[] {
    const out: FixedInstance[] = [];
    for (const fs of opts.fixedSessions ?? []) {
      if (fs.biweekly && i % 2 !== 0) continue;
      const hours = fs.durationMin / 60;
      const endurance = fs.kind !== "gym";
      const if_ = FIXED_IF[fs.kind] ?? 0.68;
      const tss = fs.kind === "gym" ? 40 : Math.round(if_ * if_ * hours * 100);
      out.push({ fs, dayOffset: fs.day, hours, tss, endurance });
    }
    return out;
  }
  // Commute counts as endurance volume too — the planner budgets for it (so it
  // doesn't pile training on top) but commute sessions are NOT published to
  // intervals.icu (they clutter the watch and the athlete already does them).
  function commuteForWeek(i: number): { hours: number; tss: number } {
    const c = opts.commute;
    if (!c || c.days.length === 0) return { hours: 0, tss: 0 };
    const monday = addDays(startMonday, i * 7);
    let hours = 0;
    for (const d of c.days) {
      const m = parseInt(toDateStr(addDays(monday, d)).slice(5, 7), 10);
      const inSeason = (c.startMonth == null || c.endMonth == null) ? true
        : (c.startMonth <= c.endMonth ? (m >= c.startMonth && m <= c.endMonth) : (m >= c.startMonth || m <= c.endMonth));
      if (inSeason) hours += (c.minutesOneWay * 2) / 60;
    }
    return { hours, tss: Math.round(0.65 * 0.65 * 100 * hours) };
  }
  // Commute counts FULLY toward load/fatigue (it's real aerobic stress logged by
  // intervals.icu), but only ~60% toward the volume budget: a fragmented,
  // stop-start, low-intensity commute gives less aerobic stimulus per minute
  // than continuous structured Z2, so it shouldn't displace planned easy volume
  // one-to-one (it would over-credit and under-train).
  const COMMUTE_DISPLACE = 0.6;
  const fixedEndHours: number[] = phases.map((_, i) => fixedForWeek(i).filter(f => f.endurance).reduce((s, f) => s + f.hours, 0));
  const fixedTSS: number[]      = phases.map((_, i) => fixedForWeek(i).reduce((s, f) => s + f.tss, 0));
  const commuteHrs: number[]    = phases.map((_, i) => commuteForWeek(i).hours);
  const commuteTSS: number[]    = phases.map((_, i) => commuteForWeek(i).tss);
  // What to remove from the training budget, show as total volume, add as load.
  const budgetHold:  number[] = phases.map((_, i) => fixedEndHours[i] + COMMUTE_DISPLACE * commuteHrs[i]);
  const displayHold: number[] = phases.map((_, i) => fixedEndHours[i] + commuteHrs[i]);
  const loadHold:    number[] = phases.map((_, i) => fixedTSS[i] + commuteTSS[i]);
  // Weekdays the athlete has locked as non-negotiable — never critique/flag the
  // events that sit on them (they're declared fixed, not up for removal).
  const fixedWeekdays = new Set((opts.fixedSessions ?? []).map(f => f.day));

  // Per-week intensity scale (multiplier on the polarization ratio). Low in
  // base, rising through build to full at peak; taper maintains peak intensity.
  // Intensity distribution: start from the science target (CTL/TSB-based), then
  // move 40% of the way toward the athlete's actual historical hard fraction —
  // informed by their habits but corrected toward optimal, not a blind copy.
  const scienceRatio = calcIntensityRatio(currentCTL, currentTSB);
  const baseRatio = opts.historicalHardFraction != null
    ? Math.min(Math.max(scienceRatio + 0.4 * (opts.historicalHardFraction - scienceRatio), 0.03), 0.22)
    : scienceRatio;
  function weekIntensityScale(i: number, phase: TrainingPhase): number {
    if (phase === "peak" || phase === "taper" || phase === "race") return 1.0;
    if (i < baseWeeks) {
      const frac = baseWeeks > 1 ? i / (baseWeeks - 1) : 0;
      return 0.35 + 0.30 * frac;       // 0.35 → 0.65
    }
    const j = i - baseWeeks;
    const frac = buildWeeks > 1 ? j / (buildWeeks - 1) : 1;
    return 0.70 + 0.30 * frac;          // 0.70 → 1.0
  }

  // Double-threshold only for fit athletes (needs a base) and only in the
  // build/peak blocks where controlled threshold volume pays off.
  const dtEligible = !!opts.doubleThreshold && currentCTL >= 55;
  function weekTemplates(i: number, phase: TrainingPhase): SessionTemplate[] {
    const ratio = Math.min(Math.max(baseRatio * weekIntensityScale(i, phase), 0.02), 0.22);
    // Base and cutback (recovery) weeks hold intensity to a single quality day.
    const maxHard = (phase === "base" || phase === "cutback") ? 1 : undefined;
    const dt = dtEligible && (phase === "build" || phase === "peak");
    return buildTemplates(race.sport, ratio, distProfile, opts.preferredDays, currentCTL, maxHard, opts.allowDoubles, dt);
  }
  function taperTemplates(ti: number): SessionTemplate[] {
    const ratio = Math.min(Math.max(baseRatio, 0.02), 0.22);
    const t = buildTemplates(race.sport, ratio, distProfile, opts.preferredDays, currentCTL);
    // Taper week 2 drops the long run (neuromuscular sharpness, low volume).
    return ti >= 1 ? t.filter(x => x.name !== "Long Z2") : t;
  }

  // Specificity progresses general (base) → threshold (early build) →
  // race-specific (late build, peak, taper). Bompa's specificity principle:
  // training resembles race demands more closely as the race approaches.
  function weekSpecificity(i: number, phase: TrainingPhase): Specificity {
    if (phase === "peak" || phase === "taper" || phase === "race") return "race";
    if (i < baseWeeks) return "general";
    const j = i - baseWeeks;                         // index within build
    return j < buildWeeks / 2 ? "threshold" : "race";
  }

  // Progressive long-run cap: start near the athlete's recent longest session
  // and grow ~10%/week toward the distance maximum (safe long-run progression).
  const longStart = opts.recentLongestHours && opts.recentLongestHours > 0
    ? Math.min(opts.recentLongestHours, distProfile.maxLongHours)
    : distProfile.maxLongHours;
  function longCapForWeek(i: number): number {
    const li = i - Math.floor(i / 4);
    return Math.min(distProfile.maxLongHours, longStart * Math.pow(1.10, li));
  }

  // ── Pass 1: realistic weekly hours + TSS from the actual session allocation ───
  const weeklyTSS: number[] = new Array(phases.length).fill(0);
  const weeklyHours: number[] = new Array(phases.length).fill(0);
  const weekAllocations: (WeekAllocation | null)[] = new Array(phases.length).fill(null);

  for (let i = 0; i < phases.length; i++) {
    const phase = phases[i];
    if (phase === "taper" || phase === "race") continue;
    // Fixed/commute volume eats into the budget so we don't train on top of it —
    // but only out of EASY volume (passed as preUsedEasy), never the long/hard.
    const alloc = allocateWeek(weekHoursTarget[i], weekTemplates(i, phase), distProfile, longCapForWeek(i), budgetHold[i]);
    weekAllocations[i] = alloc;
    weeklyHours[i] = Math.round((alloc.totalHours + displayHold[i]) * 4) / 4;
    weeklyTSS[i]   = alloc.totalTSS + loadHold[i];
  }

  // Peak week's realized volume — reference for the taper.
  const peakIdx = loadWeeks;
  const peakRealHours = peakWeeks > 0 ? weeklyHours[peakIdx] : (weeklyHours[loadWeeks - 1] || startHours);

  // ── Pass 2: TSB-checked taper (volume-driven) ────────────────────────────────
  // Maintain peak intensity, cut volume to the Mujika ceilings (wk1 ≤ 65%,
  // wk2 ≤ 45% of peak). Then simulate CTL/ATL: if race-day TSB falls short of
  // +20, cut volume FURTHER (never above the ceilings) until TSB ≈ +20.
  const startATL = Math.max(currentCTL - (currentTSB ?? 0), 0);
  let simCTL = currentCTL, simATL = startATL;
  for (let i = 0; i < loadWeeks + peakWeeks; i++) {
    ({ ctl: simCTL, atl: simATL } = advanceWeek(simCTL, simATL, weeklyTSS[i]));
  }

  const TSB_TARGET = 20;
  // Nominal taper fractions of peak volume (Mujika & Padilla 2003). A volume
  // multiplier m scales these to land race-day TSB ≈ +20: if the athlete would
  // be too fresh, carry MORE volume (m>1, up to peak); if still fatigued, cut
  // harder (m<1). Bounded so it stays a real taper but never exceeds peak.
  const taperCeil = taperWeeks === 1 ? [0.55] : [0.65, 0.45];
  const raceWiIdx = loadWeeks + peakWeeks + taperWeeks;
  const raceWeekHours = Math.max(peakRealHours * 0.20, 0.5);
  const raceWeekTSS = Math.round(0.68 * 0.68 * 100 * raceWeekHours); // easy-only week

  function simulateTaper(m: number) {
    let c = simCTL, a = simATL;
    const allocs: WeekAllocation[] = [];
    for (let ti = 0; ti < taperWeeks; ti++) {
      const wi = loadWeeks + peakWeeks + ti;
      const wh = Math.max(peakRealHours * taperCeil[ti] * m, 0);
      const alloc = allocateWeek(wh, taperTemplates(ti), distProfile, undefined, budgetHold[wi]);
      allocs.push(alloc);
      ({ ctl: c, atl: a } = advanceWeek(c, a, alloc.totalTSS + loadHold[wi]));
    }
    ({ ctl: c, atl: a } = advanceWeek(c, a, raceWeekTSS + loadHold[raceWiIdx]));
    return { tsb: c - a, allocs };
  }

  // Higher m → more volume → more fatigue → lower TSB. Search for TSB ≈ +20.
  const mMax = 1 / taperCeil[0]; // week-1 taper never exceeds peak volume
  let result: ReturnType<typeof simulateTaper>;
  if (taperWeeks > 0) {
    let lo = 0.6, hi = mMax;
    for (let iter = 0; iter < 40; iter++) {
      const m = (lo + hi) / 2;
      // too fresh (tsb above target) → need more volume → raise lower bound
      simulateTaper(m).tsb > TSB_TARGET ? (lo = m) : (hi = m);
    }
    result = simulateTaper((lo + hi) / 2);
  } else {
    result = simulateTaper(1);
  }

  for (let ti = 0; ti < taperWeeks; ti++) {
    const wi = loadWeeks + peakWeeks + ti;
    weekAllocations[wi] = result.allocs[ti];
    weeklyHours[wi] = Math.round((result.allocs[ti].totalHours + displayHold[wi]) * 4) / 4;
    weeklyTSS[wi]   = result.allocs[ti].totalTSS + loadHold[wi];
  }
  const raceWi = raceWiIdx;
  weeklyHours[raceWi] = Math.round((raceWeekHours + displayHold[raceWi]) * 4) / 4;
  weeklyTSS[raceWi]   = raceWeekTSS + loadHold[raceWi];
  const expectedRaceTSB = Math.round(result.tsb);

  // Honest volume note — always explain why the peak week is what it is, so the
  // gap between the wizard's suggested max (a ceiling) and the planned peak
  // (what the GOAL needs, capped by fitness/time/days) is never a mystery.
  const fmtH = (h: number) => `${Math.round(h * 2) / 2}h`;
  let volumeNote: string | undefined;
  if (peakRealHours < peakHours * 0.9) {
    // Days-limited: too few training days to absorb the target volume.
    volumeNote = `Planen siktar på ~${fmtH(peakHours)}/vecka som mest, men med dina valda ` +
      `träningsdagar ryms bara ~${fmtH(peakRealHours)}. Lägg till fler träningsdagar ` +
      `(särskilt lätta pass) eller slå på dubbelpass för att nå hela volymen.`;
  } else if (availableHours < Infinity && availableHours < Math.min(neededHours, reachableHours) - 0.5) {
    // Time-limited: your available hours are below what the goal would want.
    volumeNote = `Distansen gynnas av ~${fmtH(neededHours)}/vecka, men din tillgängliga tid ` +
      `är ${fmtH(availableHours)}. Planen bygger så mycket som tiden tillåter — loppet är ` +
      `genomförbart men mer tid skulle ge bättre förberedelse.`;
  } else if (availableHours < Infinity && peakHours < availableHours - 0.75) {
    // Peak is below the athlete's ceiling — explain the real limiter.
    if (neededHours <= reachableHours + 0.01) {
      // Goal-limited: the distance simply doesn't need the full ceiling.
      volumeNote = `Toppveckan ligger på ~${fmtH(peakHours)}, inte ditt tak ${fmtH(availableHours)} — ` +
        `den här distansen gynnas inte av mer volym än så, så planen lägger inte på onödiga timmar.`;
    } else {
      // Fitness/runway-limited: can't safely build higher in the time available.
      volumeNote = `Toppveckan ligger på ~${fmtH(peakHours)}, inte ditt tak ${fmtH(availableHours)} — ` +
        `utifrån din nuvarande volym och tiden fram till loppet är det så högt planen säkert kan bygga. ` +
        `Mer tid eller en högre utgångsvolym skulle höja taket.`;
    }
  }

  const weeks: PlannedWeek[] = phases.map((phase, i) => {
    const monday = addDays(startMonday, i * 7);
    const key = isoWeekKey(monday);
    return {
      weekKey: key,
      label: weekLabel(key),
      targetTSS: weeklyTSS[i],
      ...(mode === "duration" ? { targetHours: weeklyHours[i] } : {}),
      phase,
    };
  });

  // ── Analyze existing plan week by week ────────────────────────────────────────
  // For each plan week, evaluate the existing calendar sessions against scientific
  // targets and flag issues. Suggestions fill what's missing.

  const LONG_KW = /long|l[åa]ng|långpass/i;
  const HARD_KW_ANALYSIS = /interval|intervall|threshold|tr[öo]skel|tempo|z[45]|sprint|fartlek/i;
  const EASY_KW_ANALYSIS = /l[äa]tt|easy|lugn|vila|recovery|pendling|commute/i;

  for (const week of weeks) {
    const phase = week.phase;
    const target = week.targetTSS;

    // Existing events in this week (skip gym/commute, and any day the athlete
    // locked as a fixed/non-negotiable commitment).
    const weekEvents = existingEvents.filter(e => {
      const d = e.start_date_local.slice(0, 10);
      const weekday = (new Date(d + "T12:00:00").getDay() + 6) % 7;
      return isoWeekKey(new Date(d + "T12:00:00")) === week.weekKey
        && classifyEvent(e) !== "skip"
        && !/^RACE/i.test((e as any).category ?? "") // never critique a race
        && !fixedWeekdays.has(weekday);
    });

    if (weekEvents.length === 0) continue;

    // Sort by date so we can detect consecutive quality sessions
    weekEvents.sort((a, b) => a.start_date_local.localeCompare(b.start_date_local));

    // Accumulate TSS/hours and track quality days
    let accTSS = 0;
    let accHours = 0;
    const qualityDates: string[] = [];

    for (const e of weekEvents) {
      const date = e.start_date_local.slice(0, 10);
      const tss = estimateEventTSS(e);
      const cls = classifyEvent(e);
      accHours += (e as any).duration > 0 ? (e as any).duration / 3600 : parseDurationFromText(`${e.name ?? ""} ${(e as any).description ?? ""}`);

      // Back-to-back quality sessions (< 2 days apart)
      if (cls === "hard" || cls === "long") {
        const lastQuality = qualityDates[qualityDates.length - 1];
        if (lastQuality) {
          const gap = (new Date(date + "T12:00:00").getTime() - new Date(lastQuality + "T12:00:00").getTime()) / 86400000;
          if (gap < 2) {
            flaggedDates.set(date, "Otillräcklig återhämtning — minst 48h bör gå mellan kvalitetspass (Hausswirth & Mujika, 2013)");
          }
        }
        qualityDates.push(date);
      }

      // Phase-specific flags
      if (phase === "race" && (cls === "hard" || cls === "long")) {
        flaggedDates.set(date, "Kvalitetspass i tävlingsvecka — fokus bör vara vila och kort aktivering inför loppet");
      }
      if (phase === "taper" && cls === "long") {
        const taperWeekIdx = weeks.filter(w => w.phase === "taper").findIndex(w => w.weekKey === week.weekKey);
        if (taperWeekIdx >= 1) {
          flaggedDates.set(date, "Långpass i taper-vecka 2 — volymen bör vara låg, håll intensiteten men minska drastiskt i tid (Mujika & Padilla, 2003)");
        }
      }

      // Single session with very high TSS — recovery cost is disproportionate
      if (tss > 150 && !flaggedDates.has(date)) {
        flaggedDates.set(date, `Pass med ~${tss} TSS kräver 48–72h återhämtning och riskerar att kompromissa resten av veckans träning — dela upp volymen på fler kortare pass (Allen & Coggan, 2010)`);
      }

      // Long run longer than race-appropriate maximum
      if (cls === "long" && !flaggedDates.has(date)) {
        const raw = e as any;
        const durH = raw.duration && raw.duration > 0
          ? raw.duration / 3600
          : parseDurationFromText(`${e.name ?? ""} ${raw.description ?? ""}`);
        const maxH = distProfile.maxLongHours;
        if (durH > 0 && durH > maxH * 1.2) {
          const fmt = (h: number) => h >= 1 ? `${Math.round(h * 4) / 4}h` : `${Math.round(h * 60)}min`;
          flaggedDates.set(date, `Långpasset (${fmt(durH)}) överstiger rekommenderat max ${fmt(maxH)} för din tävling — extra volym ger minimal nytta men ökar skade- och överträningsrisk betydligt (Bompa, 2009)`);
        }
      }

      // ── Interval-specific analysis ────────────────────────────────────────
      if (cls === "hard" && !flaggedDates.has(date)) {
        const fullText = `${e.name ?? ""} ${(e as any).description ?? ""}`;
        const iv = parseIntervalStructure(fullText);

        if (iv) {
          const repLabel = iv.workMin < 1
            ? `${Math.round(iv.workMin * 60)}sek`
            : `${iv.workMin}min`;

          // 0. Rep duration 1–2min: too short to reach VO2max plateau.
          //    It takes 2–3min to reach VO2max even at supramaximal pace (Billat 2001).
          //    1–2min reps train speed-endurance/glycolytic, not aerobic capacity.
          if (iv.workMin >= 0.75 && iv.workMin < 2 && !flaggedDates.has(date)) {
            flaggedDates.set(date,
              `${iv.reps}×${repLabel}: det tar 2–3min att nå VO2max-platå — under 2min/rep tränar du primärt fartuthållighet (glykolytiskt), inte aerob kapacitet. Bra för 800m/1500m-löpare men suboptimalt som VO2max-stimulans för längre distanser (Billat, 2001)`
            );
          }

          // 1. Race specificity: short VO2max intervals (≤4min) for marathon/ultra prep
          //    Marathon performance is limited by lactate threshold, not VO2max.
          //    Esteve-Lanao et al. (2007): threshold work > VO2max for long-distance.
          if (iv.workMin >= 2 && iv.workMin <= 4 && (dist === "marathon" || dist === "ultra") && !flaggedDates.has(date)) {
            flaggedDates.set(date,
              `${iv.reps}×${repLabel} är VO2max-arbete — för maraton/ultra begränsas prestationen av laktattröskel, inte VO2max. Byt till tröskelintervaller (10–20min/rep) som tränar direkt det metaboliska system som avgör loppet. T.ex. 3×10min eller 2×20min i maratontempo (Esteve-Lanao et al., 2007)`
            );
          }

          // 2. Insufficient rest for Z5 intervals — degrades quality to Z3 after first reps.
          //    Billat (2001): ≥1:1 work:rest needed to sustain VO2max stimulus each rep.
          if (iv.restMin !== null && iv.workMin <= 6 && iv.restMin < iv.workMin * 0.8 && !flaggedDates.has(date)) {
            flaggedDates.set(date,
              `Viloperioden (${iv.restMin}min) är för kort relativt arbetsperioden (${iv.workMin}min) — för att hålla Z5-kvalitet i varje rep behövs minst 1:1 arbets:vilokvot. För kort vila pressar ned intensiteten mot Z3 och minskar VO2max-stimulansen (Billat, 2001)`
            );
          }

          // 3. Total Z5 volume per session exceeds optimal stimulus window.
          //    Seiler (2010): 16–25min total hard work per session is the sweet spot.
          //    More doesn't improve adaptation but delays recovery by 24-48h extra.
          if (iv.workMin <= 6 && iv.totalWorkMin > 28 && !flaggedDates.has(date)) {
            flaggedDates.set(date,
              `${iv.reps}×${iv.workMin}min = ${Math.round(iv.totalWorkMin)}min total Z5-tid — Seilers forskning visar att 16–25min per session är den optimala stimulusvolym för VO2max-adaptation. Mer ger inte bättre anpassning men fördröjer återhämtningen med 24–48h extra (Seiler, 2010)`
            );
          }

          // 4. Threshold intervals too short to provide adequate stimulus.
          //    Coggan: threshold reps need ≥8min to fully engage the lactate system.
          if (iv.workMin > 4 && iv.workMin < 8 && iv.reps >= 4
            && (dist === "half" || dist === "marathon") && !flaggedDates.has(date)) {
            flaggedDates.set(date,
              `${iv.reps}×${iv.workMin}min tröskelintervaller är lite för korta för din tävlingsdistans — tröskelarbete kräver minst 8min per rep för att fullt aktivera laktatsystemet. Överväg 3×10min eller 2×15min istället (Coggan, 2010)`
            );
          }
        }
      }

      // Over-budget: flag sessions that push the week beyond 115% of target
      if (mode === "duration" && week.targetHours != null) {
        if (accHours >= week.targetHours * 1.15 && !flaggedDates.has(date)) {
          const fmt = (h: number) => h >= 1 ? `${Math.round(h * 4) / 4}h` : `${Math.round(h * 60)}min`;
          flaggedDates.set(date, `Veckan är redan överfylld (mål ${fmt(week.targetHours)}) — detta pass gör det svårt att återhämta sig tillräckligt inför nästa vecka`);
        }
      } else if (accTSS >= target * 1.15 && !flaggedDates.has(date)) {
        flaggedDates.set(date, `Veckan är redan överfylld (mål ${target} TSS) — detta pass gör det svårt att återhämta sig tillräckligt inför nästa vecka`);
      }
      accTSS += tss;
    }

    // Too much intensity in a single week (> targetRatio * 1.5)
    const hardTSS = weekEvents
      .filter(e => classifyEvent(e) === "hard")
      .reduce((s, e) => s + estimateEventTSS(e), 0);
    const hardRatio = accTSS > 0 ? hardTSS / accTSS : 0;
    if (hardRatio > baseRatio * 1.6 && hardTSS > 0) {
      const hardEvents = weekEvents.filter(e => classifyEvent(e) === "hard");
      const lastHard = hardEvents[hardEvents.length - 1];
      if (lastHard && !flaggedDates.has(lastHard.start_date_local.slice(0, 10))) {
        const pct = Math.round(hardRatio * 100);
        const targetPct = Math.round(baseRatio * 100);
        flaggedDates.set(
          lastHard.start_date_local.slice(0, 10),
          `${pct}% intensitet denna vecka — Seilers 80/20-modell rekommenderar max ${targetPct}% hårt. Överdriven intensitet hämmar aerob adaptation.`
        );
      }
    }

    // More than 2 quality sessions per week.
    // Most athletes can absorb max 2 quality sessions/week; a 3rd accumulates
    // fatigue faster than adaptation can occur (Hausswirth & Mujika, 2013).
    const hardEventsThisWeek = weekEvents.filter(e => classifyEvent(e) === "hard");
    if (hardEventsThisWeek.length > 2) {
      for (let hi = 2; hi < hardEventsThisWeek.length; hi++) {
        const d = hardEventsThisWeek[hi].start_date_local.slice(0, 10);
        if (!flaggedDates.has(d)) {
          flaggedDates.set(d,
            `${hardEventsThisWeek.length} kvalitetspass denna vecka — de flesta uthållighetsatleter kan maximalt absorbera 2 hårda pass/vecka. Ett tredje pass ackumulerar trötthet snabbare än adaptation hinner ske och ökar skaderisken avsevärt (Hausswirth & Mujika, 2013)`
          );
        }
      }
    }
  }

  // ── Cross-event analysis ─────────────────────────────────────────────────────

  const allScoredEvents = existingEvents
    .filter(e => classifyEvent(e) !== "skip")
    .sort((a, b) => a.start_date_local.localeCompare(b.start_date_local));

  const eventDateMap = new Map<string, CalendarEvent[]>();
  for (const e of allScoredEvents) {
    const d = e.start_date_local.slice(0, 10);
    if (!eventDateMap.has(d)) eventDateMap.set(d, []);
    eventDateMap.get(d)!.push(e);
  }

  // Hard session within 2 days BEFORE a long run degrades long-run quality.
  // The long run should be done aerobically fresh (Friel, "The Triathlete's Training Bible").
  // Skip if the long run itself is already flagged (we're suggesting it be removed).
  for (const e of allScoredEvents) {
    if (classifyEvent(e) !== "long") continue;
    if (flaggedDates.has(e.start_date_local.slice(0, 10))) continue;
    const longMs = new Date(e.start_date_local.slice(0, 10) + "T12:00:00").getTime();
    for (let d = 1; d <= 2; d++) {
      const checkDate = new Date(longMs - d * 86400000).toISOString().slice(0, 10);
      for (const prev of (eventDateMap.get(checkDate) ?? [])) {
        if (classifyEvent(prev) !== "hard" || flaggedDates.has(checkDate)) continue;
        if (LOW_INTENSITY_EVENT.test(prev.name ?? "")) continue;

        // Suggest moving the hard session earlier (before the 48h window)
        let suggestedDate: string | null = null;
        for (let offset = 3; offset <= 6; offset++) {
          const candidate = new Date(longMs - offset * 86400000).toISOString().slice(0, 10);
          const hasQuality = (eventDateMap.get(candidate) ?? []).some(ev => classifyEvent(ev) === "hard");
          if (!hasQuality) { suggestedDate = candidate; break; }
        }

        const suggestion = suggestedDate
          ? ` Överväg att flytta passet till ${new Date(suggestedDate + "T12:00:00").toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "short" })} för att ha 48h vila innan långpasset.`
          : " Håll minst 48h vila innan långpasset.";

        flaggedDates.set(checkDate,
          `Hårt pass ${d} dag(ar) innan långpass — håll minst 48h vila innan långpasset så det kan genomföras aerobt och med tillräcklig fettförbränning.${suggestion} (Friel, 2012)`
        );
      }
    }
  }

  // Hard session within 1 day AFTER a long run.
  // Skip if the long run itself is already flagged (contradictory to also flag adjacency).
  for (const e of allScoredEvents) {
    if (classifyEvent(e) !== "long") continue;
    if (flaggedDates.has(e.start_date_local.slice(0, 10))) continue;
    const longMs = new Date(e.start_date_local.slice(0, 10) + "T12:00:00").getTime();
    const nextDate = new Date(longMs + 86400000).toISOString().slice(0, 10);
    for (const next of (eventDateMap.get(nextDate) ?? [])) {
      if (classifyEvent(next) !== "hard" || flaggedDates.has(nextDate)) continue;
      if (LOW_INTENSITY_EVENT.test(next.name ?? "")) continue;

      // Find the first free day ≥ 2 days after the long run to suggest as alternative
      let suggestedDate: string | null = null;
      for (let offset = 2; offset <= 5; offset++) {
        const candidate = new Date(longMs + offset * 86400000).toISOString().slice(0, 10);
        const hasQuality = (eventDateMap.get(candidate) ?? []).some(ev => classifyEvent(ev) === "hard");
        if (!hasQuality) { suggestedDate = candidate; break; }
      }

      const suggestion = suggestedDate
        ? ` Överväg att flytta passet till ${new Date(suggestedDate + "T12:00:00").toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "short" })} när glykogenlagren är påfyllda.`
        : " Lägg in minst 1 lätt/vilodagen emellan.";

      flaggedDates.set(nextDate,
        `Hårt pass dagen efter långpass — långpass tömmer glykogenlagren i 24–48h. Kvalitetsträning innan lagerna är påfyllda ger låg sessionskvalitet och ökar skaderisken.${suggestion} (Burke & Deakin, 2015)`
      );
    }
  }

  // Hard session within 7 days of race prevents full supercompensation.
  // The last quality session should be 7–10 days out (Bompa & Haff, 2009).
  const raceDateMs = new Date(race.date + "T12:00:00").getTime();
  for (const e of allScoredEvents) {
    if (classifyEvent(e) !== "hard") continue;
    const date = e.start_date_local.slice(0, 10);
    const daysToRace = (raceDateMs - new Date(date + "T12:00:00").getTime()) / 86400000;
    if (daysToRace > 0 && daysToRace < 7 && !flaggedDates.has(date)) {
      flaggedDates.set(date, `Intensivt pass ${Math.round(daysToRace)} dagar före loppet — sista kvalitetspasset bör ligga 7–10 dagar innan för att kroppen ska hinna superkompensera fullt ut (Bompa & Haff, 2009)`);
    }
  }

  // Hard and long sessions always use the race sport (specificity). Easy days
  // cross-train with secondary sports — and the athlete's PRIMARY sport is kept
  // in view there even while training for a race in another sport (e.g. a skier
  // rollerskis on easy days through a running build, holding ski-specific base).
  const secondaryRaw = [
    ...(opts.primarySport && opts.primarySport !== race.sport ? [opts.primarySport] : []),
    ...(opts.sportsEnjoyed ?? []),
  ].filter(s => s !== race.sport && s !== "WeightTraining" && s !== "AlpineSki");
  const secondarySports = Array.from(new Set(secondaryRaw));
  const favoriteSessionIdSet = new Set(opts.favoriteSessionIds ?? []);
  const snowStart = opts.snowStartMonth ?? 11;
  const snowEnd = opts.snowEndMonth ?? 4;
  const seasonal = (sport: SportType, date: string) => seasonalSport(sport, date, snowStart, snowEnd);
  // The race sport is PRIORITISED on easy days too — ski for a ski race, run for
  // a run race, bike for a bike race — so the build is race-specific. Cross-
  // training is kept for variety/recovery but as the minority (~1 in 3 easy
  // sessions). Hard and long sessions are always the race sport.
  let secondaryIdx = 0;
  function nextSport(isHard: boolean, isLong: boolean): SportType {
    if (isHard || isLong || secondarySports.length === 0) return race.sport;
    const i = secondaryIdx++;
    return i % 3 === 2 ? secondarySports[Math.floor(i / 3) % secondarySports.length] : race.sport;
  }

  const gymDays = (opts.preferredDays?.gym ?? []).slice(0, 3);
  const hasGym  = (opts.sportsEnjoyed ?? []).includes("WeightTraining") && gymDays.length > 0;

  const sessions: PlannedSession[] = [];

  for (let wi = 0; wi < weeks.length; wi++) {
    const week  = weeks[wi];
    const phase = phases[wi];

    // Non-negotiable fixed sessions go down first and lock their day(s) so the
    // rest of the plan is built around them. Same-day stacking is allowed
    // (e.g. lunch run + evening gym).
    const weekMonday = mondayOfISOWeek(week.weekKey);
    for (const fi of fixedForWeek(wi)) {
      const date = toDateStr(addDays(weekMonday, fi.dayOffset));
      if (date < todayStr) continue;
      if (blockedDates.has(date)) continue; // vacation / blocked
      // Skip if the athlete already has this on their calendar that day.
      if (existingEvents.some(e => e.start_date_local.slice(0, 10) === date && (e as any).type === fi.fs.sport)) continue;
      const kindLabel = fi.fs.kind === "gym" ? "Gym" : fi.fs.kind === "quality" ? "Kvalitetspass" : "Lätt pass";
      const sport = seasonal(fi.fs.sport, date);
      const fixedHours = Math.round(fi.hours * 4) / 4;
      // Build a real intervals.icu step: HR-zone target for endurance, plain
      // duration for gym/strength. toIcuWorkout enforces dash + h/m/s + Zn HR.
      const zoneTok = fi.fs.kind === "gym" ? "" : fi.fs.kind === "quality" ? " Z4 HR" : " Z2 HR";
      const note = `${kindLabel.toLowerCase()} (fast pass${fi.fs.biweekly ? ", varannan vecka" : ""})`;
      sessions.push({
        name: fi.fs.label || kindLabel,
        date, weekKey: week.weekKey, sport,
        durationHours: fixedHours,
        targetIF: FIXED_IF[fi.fs.kind] ?? 0.68,
        estimatedTSS: fi.tss,
        description: toIcuWorkout(`${formatDuration(fixedHours)}${zoneTok} ${note}`),
      });
      occupiedDays.add(date); bump(date);
      if (fi.fs.kind === "quality") {
        qualityDayBuf.add(date);
        adjacentDates(date).forEach(d => qualityDayBuf.add(d));
      }
    }

    if (phase === "race") {
      const easyTmpl = weekTemplates(wi, "race").find(t => t.name === "Easy");
      const half = (weeklyHours[wi] || raceWeekHours) / 2;
      if (easyTmpl) {
        const s1 = makeSession({ ...easyTmpl, candidateDays: [0, 1, 2] }, half, week.weekKey, race.sport, distProfile, todayStr, occupiedDays, qualityDayBuf, "race", dist, currentCTL, dayCount);
        if (s1) { sessions.push({ ...s1, name: "Easy", sport: seasonal(s1.sport, s1.date) }); occupiedDays.add(s1.date); bump(s1.date); }
        const s2 = makeSession({ ...easyTmpl, candidateDays: [2, 3, 1] }, half, week.weekKey, race.sport, distProfile, todayStr, occupiedDays, qualityDayBuf, "race", dist, currentCTL, dayCount);
        if (s2) { sessions.push({ ...s2, name: "Easy", sport: seasonal(s2.sport, s2.date) }); occupiedDays.add(s2.date); bump(s2.date); }
      }
      continue;
    }

    // Sessions are sized from the pre-computed, cap-aware hour allocation.
    const alloc = weekAllocations[wi];
    if (!alloc) continue;
    for (const entry of alloc.perTemplate) {
      const tmpl = entry.tmpl;
      if (entry.hours < 0.083) continue;
      const isHard = tmpl.name !== "Long Z2" && tmpl.name !== "Easy";
      const isLong = tmpl.name === "Long Z2";
      const sport  = nextSport(isHard, isLong);
      const session = makeSession(tmpl, entry.hours, week.weekKey, sport, distProfile, todayStr, occupiedDays, qualityDayBuf, weekSpecificity(wi, phase), dist, currentCTL, dayCount, trail, opts.customSessions ?? [], favoriteSessionIdSet);
      if (session) {
        sessions.push({ ...session, sport: seasonal(session.sport, session.date) });
        occupiedDays.add(session.date);
        bump(session.date);
        if (session.name === "Long Z2" || session.targetIF >= 0.87) {
          qualityDayBuf.add(session.date);
          adjacentDates(session.date).forEach(d => qualityDayBuf.add(d));
        }
      } else if (tmpl.candidateDays.length > 0) {
        // Placement failed — show the blocker as red and add a green chip on the suggested new day
        const monday = mondayOfISOWeek(week.weekKey);
        const idealDate = toDateStr(addDays(monday, tmpl.candidateDays[0]));
        if (idealDate >= todayStr && occupiedDays.has(idealDate) && !flaggedDates.has(idealDate)) {
          const blocker = existingEvents.find(e =>
            e.start_date_local.slice(0, 10) === idealDate &&
            (classifyEvent(e) === "hard" || classifyEvent(e) === "long")
          );
          const tmplType = isLong ? "long" : "hard";
          const isOwnSameType = blocker != null
            && (opts.ownEventIds?.includes(blocker.id) ?? false)
            && classifyEvent(blocker) === tmplType;
          if (blocker && /^RACE/i.test((blocker as any).category ?? "")) {
            // Never suggest moving a race.
          } else if (isOwnSameType) {
            // Our own session, correctly placed for this slot — satisfied, leave it.
          } else if (blocker) {
            // Genuinely in the way (wrong type / conflict) — suggest a move.
            const idealMs = new Date(idealDate + "T12:00:00").getTime();
            let altDay: string | null = null;
            for (let delta = 1; delta <= 4 && !altDay; delta++) {
              for (const sign of [1, -1]) {
                const candidate = new Date(idealMs + sign * delta * 86400000).toISOString().slice(0, 10);
                if (candidate < todayStr || occupiedDays.has(candidate)) continue;
                if (isLong && qualityDayBuf.has(candidate)) continue;
                altDay = candidate;
                break;
              }
            }
            if (altDay) {
              const sessionLabel = isLong ? "långpass" : tmpl.name === "Easy" ? "lätt pass" : "kvalitetspass";
              const altLabel = new Date(altDay + "T12:00:00").toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "short" });
              const fromLabel = new Date(idealDate + "T12:00:00").toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "short" });

              // Red flag on original day
              flaggedDates.set(idealDate,
                `Planeringen vill lägga ett ${sessionLabel} här — flytta detta pass till ${altLabel} (se grön markering)`
              );

              // Green chip on the suggested new day — carry the blocker's REAL
              // workout (so the profile chart is correct) and derive duration +
              // TSS from the SAME source so they stay consistent. The move
              // explanation goes in flaggedDates → shown as the modal's reason.
              const rawDesc = ((blocker as any).description ?? "") as string;
              const parsedBlocker = rawDesc.includes("\n") ? parseStructuredWorkout(rawDesc) : null;
              const blockerDur = (parsedBlocker?.durationHours
                ?? ((blocker as any).duration > 0
                  ? (blocker as any).duration / 3600
                  : parseDurationFromText(`${blocker.name ?? ""} ${rawDesc}`)))
                || 0.5;
              const blockerTSS = parsedBlocker?.tss ?? estimateEventTSS(blocker);
              const blockerIF = blockerDur > 0 ? Math.min(Math.sqrt(blockerTSS / (blockerDur * 100)), 1) : 0.75;
              const VALID_SPORTS = new Set(["Run","Ride","NordicSki","RollerSki","WeightTraining"]);
              const blockerSport = VALID_SPORTS.has((blocker as any).type) ? (blocker as any).type as SportType : race.sport;
              sessions.push({
                name: blocker.name ?? "Pass",
                date: altDay,
                weekKey: isoWeekKey(new Date(altDay + "T12:00:00")),
                sport: blockerSport,
                durationHours: Math.round(Math.max(blockerDur, 0.25) * 4) / 4,
                targetIF: blockerIF,
                estimatedTSS: blockerTSS,
                description: rawDesc || (blocker.name ?? "Pass"),
              });
              flaggedDates.set(altDay,
                `Föreslagen flytt från ${fromLabel} — frigör plats för ett ${sessionLabel} på den optimala dagen.`
              );
              occupiedDays.add(altDay); bump(altDay); // prevent double-booking
            }
          }
        }
      }
    }

    if (hasGym) {
      const monday = mondayOfISOWeek(week.weekKey);
      for (const dayOffset of gymDays) {
        const date = toDateStr(addDays(monday, dayOffset));
        if (date < todayStr) continue;
        if (blockedDates.has(date)) continue; // vacation / blocked
        // Don't suggest gym if already on calendar that day
        if (existingEvents.some(e =>
          e.start_date_local.slice(0, 10) === date && /gym|styrka|strength/i.test(e.name ?? "")
        )) continue;
        // Periodized, sport-aware routine for the current training state.
        const routine = pickStrengthRoutine(phase, race.sport, `${wi}-${dayOffset}`);
        sessions.push({
          name: `Gym · ${strengthFocusLabel(routine.focus)}`, date, weekKey: week.weekKey, sport: "WeightTraining",
          durationHours: 1.0, targetIF: 0.60, estimatedTSS: 40,
          description: toIcuWorkout(buildGymDescription(routine, "1h")),
        });
      }
    }

    // Commute is NOT suggested as a session — the athlete already does it. It is
    // only accounted for in the weekly volume/load budget (see budgetHold /
    // loadHold), so the plan trains around it without cluttering the calendar.
  }

  // Never leave a removal/critique flag on a locked fixed-session day.
  if (fixedWeekdays.size > 0) {
    for (const d of Array.from(flaggedDates.keys())) {
      const weekday = (new Date(d + "T12:00:00").getDay() + 6) % 7;
      if (fixedWeekdays.has(weekday)) flaggedDates.delete(d);
    }
  }

  return {
    weeks, sessions, flaggedDates, expectedRaceTSB, volumeNote,
    plannedHardPct: Math.round(baseRatio * 100),
    historicalHardPct: opts.historicalHardFraction != null
      ? Math.round(opts.historicalHardFraction * 100) : undefined,
  };
}
